// 2D lightning-strike overlay — jagged bolt from above into a target card rect.
import { getSoundManager } from '../audio/sound-manager.js';

export interface StrikeTarget {
  left: number;
  top: number;
  width: number;
  height: number;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, ms));
}

function jitteredBoltPath(x0: number, y0: number, x1: number, y1: number, segments = 7): string {
  const pts: Array<[number, number]> = [[x0, y0]];
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const x = x0 + (x1 - x0) * t + (Math.random() - 0.5) * 18;
    const y = y0 + (y1 - y0) * t + (Math.random() - 0.5) * 8;
    pts.push([x, y]);
  }
  pts.push([x1, y1]);
  return pts.map((p, i) => (i === 0 ? `M ${p[0]} ${p[1]}` : `L ${p[0]} ${p[1]}`)).join(' ');
}

export class LightningStrikeAnimator {
  private readonly host: HTMLElement;
  private readonly svg: SVGSVGElement;
  private generation = 0;

  constructor(host: HTMLElement) {
    this.host = host;
    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svg.setAttribute('class', 'lightning-strike-layer');
    this.svg.setAttribute('aria-hidden', 'true');
    host.appendChild(this.svg);
  }

  cancel(): void {
    this.generation += 1;
    this.svg.replaceChildren();
    this.svg.classList.remove('lightning-strike-layer--active');
  }

  destroy(): void {
    this.cancel();
    this.svg.remove();
  }

  /** Strikes the card center; resolves when the impact flash completes. */
  async strikeCard(target: StrikeTarget, reducedMotion: boolean): Promise<void> {
    const gen = ++this.generation;
    const hostRect = this.host.getBoundingClientRect();
    const cx = target.left + target.width / 2 - hostRect.left;
    const cy = target.top + target.height * 0.45 - hostRect.top;
    const x0 = cx + (Math.random() - 0.5) * 12;
    const y0 = Math.max(0, target.top - hostRect.top - 48);

    this.svg.setAttribute('viewBox', `0 0 ${hostRect.width} ${hostRect.height}`);
    this.svg.style.width = `${hostRect.width}px`;
    this.svg.style.height = `${hostRect.height}px`;

    if (reducedMotion) {
      this.svg.replaceChildren();
      const glow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      glow.setAttribute('cx', String(cx));
      glow.setAttribute('cy', String(cy));
      glow.setAttribute('r', String(Math.max(target.width, target.height) * 0.35));
      glow.setAttribute('class', 'lightning-strike-glow');
      this.svg.appendChild(glow);
      this.svg.classList.add('lightning-strike-layer--active');
      try {
        getSoundManager().play('lightning.impact');
      } catch {
        /* audio optional */
      }
      await sleep(220);
      if (gen === this.generation) this.svg.replaceChildren();
      return;
    }

    try {
      getSoundManager().play('lightning.precharge');
    } catch {
      /* audio optional */
    }
    await sleep(40);
    if (gen !== this.generation) return;

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', jitteredBoltPath(x0, y0, cx, cy));
    path.setAttribute('class', 'lightning-strike-bolt');

    const core = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    core.setAttribute('d', path.getAttribute('d')!);
    core.setAttribute('class', 'lightning-strike-bolt lightning-strike-bolt--core');

    const flash = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    flash.setAttribute('cx', String(cx));
    flash.setAttribute('cy', String(cy));
    flash.setAttribute('r', '4');
    flash.setAttribute('class', 'lightning-strike-flash');

    const sparks: SVGLineElement[] = [];
    for (let i = 0; i < 5; i++) {
      const ang = (Math.PI * 2 * i) / 5 + Math.random() * 0.4;
      const len = 10 + Math.random() * 14;
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(cx));
      line.setAttribute('y1', String(cy));
      line.setAttribute('x2', String(cx + Math.cos(ang) * len));
      line.setAttribute('y2', String(cy + Math.sin(ang) * len));
      line.setAttribute('class', 'lightning-strike-spark');
      sparks.push(line);
    }

    this.svg.replaceChildren(path, core, flash, ...sparks);
    this.svg.classList.add('lightning-strike-layer--active');

    try {
      getSoundManager().play('lightning.strike');
    } catch {
      /* audio optional */
    }
    await sleep(90);
    if (gen !== this.generation) return;

    try {
      getSoundManager().play('lightning.impact');
    } catch {
      /* audio optional */
    }
    await sleep(160);
    if (gen === this.generation) {
      this.svg.replaceChildren();
      this.svg.classList.remove('lightning-strike-layer--active');
    }
  }
}
